export type TaskStatusFilter =
  | "all"
  | "todo"
  | "inprogress"
  | "completed"
  | "overdue"
  | "engineering"
  | "finance"
  | "rfi_to_invoice"
  | "rfi_pending_payment"
  | "rfi_paid";

export type TaskWorkflowBucket =
  | "rfi_to_invoice"
  | "rfi_pending_payment"
  | "rfi_paid"
  | "todo"
  | "inprogress"
  | "completed"
  | "overdue";

type TaskWorkflowRecord = {
  category?: string | null;
  taskType?: string | null;
  status?: string | null;
  statusCode?: string | null;
  linkedInvoiceId?: number | null;
  linkedInvoiceNumber?: string | null;
  linkedInvoiceSource?: string | null;
  invoiceStatus?: string | null;
  paymentStatus?: string | null;
  stripePaymentUrl?: string | null;
  stripePaymentReference?: string | null;
};

function normalize(value?: string | null) {
  return String(value || "").trim().toLowerCase();
}

function isRfiTask(task: TaskWorkflowRecord) {
  return normalize(task.taskType) === "rfi";
}

function hasInvoiceArtifacts(task: TaskWorkflowRecord) {
  return Boolean(
    task.linkedInvoiceId
    || normalize(task.linkedInvoiceNumber)
    || normalize(task.linkedInvoiceSource)
    || normalize(task.invoiceStatus)
    || normalize(task.stripePaymentUrl)
    || normalize(task.stripePaymentReference)
  );
}

function isTaskStatus(task: TaskWorkflowRecord, code: string, aliases: string[]) {
  const normalizedCode = normalize(task.statusCode);
  if (normalizedCode) {
    return normalizedCode === code;
  }

  const normalizedStatus = normalize(task.status);
  return aliases.includes(normalizedStatus);
}

export function isPaidRfiTask(task: TaskWorkflowRecord) {
  const invoiceStatus = normalize(task.invoiceStatus);
  const paymentStatus = normalize(task.paymentStatus);
  return invoiceStatus === "paid" || paymentStatus === "paid";
}

export function getTaskWorkflowBucket(task: TaskWorkflowRecord): TaskWorkflowBucket {
  if (isRfiTask(task)) {
    if (isPaidRfiTask(task)) {
      return "rfi_paid";
    }

    if (hasInvoiceArtifacts(task)) {
      return "rfi_pending_payment";
    }

    return "rfi_to_invoice";
  }

  if (isTaskStatus(task, "3", ["completed", "complete", "done"])) {
    return "completed";
  }

  if (isTaskStatus(task, "4", ["overdue"])) {
    return "overdue";
  }

  if (isTaskStatus(task, "2", ["in progress", "progress", "ongoing"])) {
    return "inprogress";
  }

  return "todo";
}

export function matchesTaskStatusFilter(task: TaskWorkflowRecord, filter: TaskStatusFilter) {
  if (filter === "all") {
    return true;
  }

  if (filter === "todo") return isTaskStatus(task, "1", ["pending", "to do", "todo"]);
  if (filter === "inprogress") return isTaskStatus(task, "2", ["in progress", "progress", "ongoing"]);
  if (filter === "completed") return isTaskStatus(task, "3", ["completed", "complete", "done"]);
  if (filter === "overdue") return isTaskStatus(task, "4", ["overdue"]);
  if (filter === "engineering") return normalize(task.category) === "engineering";
  if (filter === "finance") return normalize(task.category) === "finance";

  const bucket = getTaskWorkflowBucket(task);
  return bucket === filter;
}

export function getTaskWorkflowSortRank(task: TaskWorkflowRecord) {
  const bucket = getTaskWorkflowBucket(task);

  switch (bucket) {
    case "rfi_to_invoice":
      return 0;
    case "rfi_pending_payment":
      return 1;
    case "rfi_paid":
      return 2;
    case "overdue":
      return 3;
    case "inprogress":
      return 4;
    case "todo":
      return 5;
    case "completed":
      return 6;
    default:
      return 99;
  }
}
